import { useState, useCallback, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { chatApi, type ChatMessage } from '@/api/chat'

export interface LocalChatMessage extends ChatMessage {
  isStreaming?: boolean
}

export function useChat(projectId: string, sessionId: string | null) {
  const queryClient = useQueryClient()
  const [streamingMessages, setStreamingMessages] = useState<LocalChatMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const bufferRef = useRef('')

  const { data: serverMessages = [], isLoading } = useQuery({
    queryKey: ['chat-messages', sessionId],
    queryFn: () => chatApi.getMessages(projectId, sessionId!),
    enabled: !!sessionId,
  })

  // Show streaming messages when active, otherwise show server messages only
  const allMessages: LocalChatMessage[] = streamingMessages.length > 0
    ? [...serverMessages, ...streamingMessages]
    : serverMessages

  const sendMessage = useCallback(
    async (content: string, selectedModel?: string, contextMode?: 'documents' | 'extracted_data' | 'both') => {
      if (!sessionId || isStreaming) return

      // Optimistically add user message
      const tempUserMsg: LocalChatMessage = {
        id: `temp-user-${Date.now()}`,
        session_id: sessionId,
        role: 'user',
        content,
        cited_document_ids: null,
        tokens_used: null,
        model_used: null,
        created_at: new Date().toISOString(),
      }

      // Add empty assistant message for streaming
      const tempAssistantMsg: LocalChatMessage = {
        id: `temp-assistant-${Date.now()}`,
        session_id: sessionId,
        role: 'assistant',
        content: '',
        cited_document_ids: null,
        tokens_used: null,
        model_used: null,
        created_at: new Date().toISOString(),
        isStreaming: true,
      }

      setStreamingMessages([tempUserMsg, tempAssistantMsg])
      setIsStreaming(true)
      bufferRef.current = ''

      try {
        const response = await chatApi.sendMessage(projectId, sessionId, content, selectedModel, contextMode)
        if (!response.body) throw new Error('No response body')

        const reader = response.body.getReader()
        const decoder = new TextDecoder()

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          // Accumulate into buffer to handle chunks that split across reads
          bufferRef.current += decoder.decode(value, { stream: true })
          const lines = bufferRef.current.split('\n')
          // Keep the last (possibly incomplete) line in the buffer
          bufferRef.current = lines.pop() || ''

          for (const line of lines) {
            const trimmed = line.trim()
            if (!trimmed.startsWith('data: ')) continue
            try {
              const payload = JSON.parse(trimmed.slice(6))
              if (payload.token) {
                setStreamingMessages((prev) => {
                  const updated = [...prev]
                  const lastMsg = updated[updated.length - 1]
                  if (lastMsg?.isStreaming) {
                    updated[updated.length - 1] = {
                      ...lastMsg,
                      content: lastMsg.content + payload.token,
                    }
                  }
                  return updated
                })
              }
              if (payload.done) {
                setStreamingMessages((prev) => {
                  const updated = [...prev]
                  const lastMsg = updated[updated.length - 1]
                  if (lastMsg?.isStreaming) {
                    updated[updated.length - 1] = {
                      ...lastMsg,
                      isStreaming: false,
                      cited_document_ids: payload.cited_document_ids || null,
                    }
                  }
                  return updated
                })
                setIsStreaming(false)
                // Refresh messages from server then clear streaming state
                await queryClient.invalidateQueries({ queryKey: ['chat-messages', sessionId] })
                // Also refresh sessions to update title and message count
                queryClient.invalidateQueries({ queryKey: ['chat-sessions', projectId] })
                setStreamingMessages([])
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      } catch (error) {
        console.error('Chat stream error:', error)
        setIsStreaming(false)
        setStreamingMessages([])
      }
    },
    [projectId, sessionId, isStreaming, queryClient]
  )

  return {
    messages: allMessages,
    isLoading,
    sendMessage,
    isStreaming,
  }
}
